import { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Save } from "lucide-react";

/** Config REP-P (espelha a entidade config_ponto — chaves PascalCase). */
interface ConfigPonto {
  IToleranciaPadraoMin: number;
  IMinutoExtraAPartirDe: number;
  FPercentualHoraExtra: number;
  FPercentualExtraDomingoFeriado: number;
  FPercentualAdicionalNoturno: number;
  IScoreFacialMinimo: number;
  IDiaFechamentoMes: number;
  IModeloFacialVersaoAtual: number;
  IbExigeFoto: boolean;
  IbExigeGeolocalizacao: boolean;
  IbBancoHorasAtivo: boolean;
  IbAplicarHoraReduzidaNoturna: boolean;
  IbFacultativoContaComoFeriado: boolean;
  SNumeroRegistroInpi: string | null;
  SSerieRepP: string | null;
}

export default function ConfigPontoPage() {
  const [cfg, setCfg] = useState<ConfigPonto | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    apiClient
      .get<ConfigPonto>("/api/rh/ponto/config")
      .then((r) => setCfg(r.data))
      .catch((e) => toast.error(e.message ?? "Falha ao carregar a configuração."))
      .finally(() => setCarregando(false));
  }, []);

  const set = <K extends keyof ConfigPonto>(k: K, v: ConfigPonto[K]) =>
    setCfg((c) => (c ? { ...c, [k]: v } : c));

  const num = (k: keyof ConfigPonto) => (e: React.ChangeEvent<HTMLInputElement>) =>
    set(k, (e.target.value === "" ? 0 : Number(e.target.value)) as never);

  const salvar = async () => {
    if (!cfg) return;
    setSalvando(true);
    try {
      await apiClient.put("/api/rh/ponto/config", cfg);
      toast.success("Configuração de ponto salva.");
    } catch (e: any) {
      toast.error(e.message ?? "Falha ao salvar.");
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) return <div className="p-6 text-muted-foreground">Carregando…</div>;
  if (!cfg) return <div className="p-6 text-muted-foreground">Sem configuração.</div>;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Configuração de Ponto (REP-P)</h1>
          <p className="text-sm text-muted-foreground">Portaria MTP 671/2021 — parâmetros de apuração e identificação do programa.</p>
        </div>
        <Button onClick={salvar} disabled={salvando}>
          <Save className="h-4 w-4 mr-2" />
          {salvando ? "Salvando…" : "Salvar"}
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Apuração</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Tolerância padrão (min)</Label>
            <Input type="number" value={cfg.IToleranciaPadraoMin} onChange={num("IToleranciaPadraoMin")} />
          </div>
          <div className="space-y-1">
            <Label>Extra a partir de (min)</Label>
            <Input type="number" value={cfg.IMinutoExtraAPartirDe} onChange={num("IMinutoExtraAPartirDe")} />
          </div>
          <div className="space-y-1">
            <Label>Dia de fechamento do mês</Label>
            <Input type="number" value={cfg.IDiaFechamentoMes} onChange={num("IDiaFechamentoMes")} />
          </div>
          <div className="space-y-1">
            <Label>% Hora extra (dia útil)</Label>
            <Input type="number" step="0.01" value={cfg.FPercentualHoraExtra} onChange={num("FPercentualHoraExtra")} />
          </div>
          <div className="space-y-1">
            <Label>% Extra domingo/feriado</Label>
            <Input type="number" step="0.01" value={cfg.FPercentualExtraDomingoFeriado} onChange={num("FPercentualExtraDomingoFeriado")} />
          </div>
          <div className="space-y-1">
            <Label>% Adicional noturno</Label>
            <Input type="number" step="0.01" value={cfg.FPercentualAdicionalNoturno} onChange={num("FPercentualAdicionalNoturno")} />
          </div>
          <div className="flex items-center gap-2 pt-6">
            <Switch checked={cfg.IbBancoHorasAtivo} onCheckedChange={(v) => set("IbBancoHorasAtivo", v)} />
            <Label>Banco de horas ativo</Label>
          </div>
          <div className="flex items-center gap-2 pt-6">
            <Switch checked={cfg.IbAplicarHoraReduzidaNoturna} onCheckedChange={(v) => set("IbAplicarHoraReduzidaNoturna", v)} />
            <Label>Hora noturna reduzida (52'30")</Label>
          </div>
          <div className="flex items-center gap-2 pt-6">
            <Switch checked={cfg.IbFacultativoContaComoFeriado} onCheckedChange={(v) => set("IbFacultativoContaComoFeriado", v)} />
            <Label>Facultativo conta como feriado</Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Batida / reconhecimento facial</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex items-center gap-2 pt-6">
            <Switch checked={cfg.IbExigeFoto} onCheckedChange={(v) => set("IbExigeFoto", v)} />
            <Label>Exige foto na batida</Label>
          </div>
          <div className="flex items-center gap-2 pt-6">
            <Switch checked={cfg.IbExigeGeolocalizacao} onCheckedChange={(v) => set("IbExigeGeolocalizacao", v)} />
            <Label>Exige geolocalização</Label>
          </div>
          <div className="space-y-1">
            <Label>Distância facial máx. (0–2)</Label>
            <Input type="number" step="0.0001" value={cfg.IScoreFacialMinimo} onChange={num("IScoreFacialMinimo")} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Identificação REP-P</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Nº de registro no INPI</Label>
            <Input value={cfg.SNumeroRegistroInpi ?? ""} onChange={(e) => set("SNumeroRegistroInpi", e.target.value)} placeholder="ex.: 512026006288-8" />
          </div>
          <div className="space-y-1">
            <Label>Série do REP-P</Label>
            <Input value={cfg.SSerieRepP ?? ""} onChange={(e) => set("SSerieRepP", e.target.value)} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
